/* Admin → System → Fees (v5.5)
 * DB-backed Documentation + Title fees with audit history.
 */
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { fetchFees } from "../lib/feeSettings";

export default function AdminFees() {
  const [fees, setFees] = useState(null);
  const [docFee, setDocFee] = useState("");
  const [titleFee, setTitleFee] = useState("");
  const [labelDoc, setLabelDoc] = useState("Documentation Fee");
  const [labelTitle, setLabelTitle] = useState("Title Fee");
  const [effDate, setEffDate] = useState("");
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/system/fees");
      setFees(data);
      setDocFee(String(data.doc_fee ?? 398));
      setTitleFee(String(data.title_fee ?? 50));
      setLabelDoc(data.fee_label_doc || "Documentation Fee");
      setLabelTitle(data.fee_label_title || "Title Fee");
      setEffDate(data.effective_date || "");
      const aud = await api.get("/admin/system/fees/audit");
      setAudit(aud.data.audit || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setSavedMsg("");
    try {
      await api.put("/admin/system/fees", {
        doc_fee: Number(docFee),
        title_fee: Number(titleFee),
        fee_label_doc: labelDoc,
        fee_label_title: labelTitle,
        effective_date: effDate || null,
      });
      // bust frontend cache
      try { sessionStorage.removeItem("ssc_effective_fees_v1"); } catch {}
      await fetchFees();
      setSavedMsg("Fees saved.");
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-fees">
      <header>
        <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)]">System</div>
        <h1 className="text-3xl font-black text-[var(--ssc-navy)] mt-1">Dealer Fees</h1>
        <p className="text-[var(--ssc-ink-2)] mt-2 max-w-2xl">
          These fees show in every VDP pricing breakdown and the Build My Deal stack.
          Changes apply to new pricing breakdowns immediately. vAuto advertised price is
          never modified.
        </p>
      </header>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm px-3 py-2" data-testid="admin-fees-error">
          {error}
        </div>
      )}

      <form onSubmit={save} className="rounded-xl border border-[var(--ssc-line)] bg-white p-5 shadow-sm space-y-4 max-w-2xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="ssc-label">Documentation Fee ($)</label>
            <input className="ssc-input" inputMode="numeric" value={docFee} onChange={(e) => setDocFee(e.target.value)} required data-testid="admin-fees-doc" />
          </div>
          <div>
            <label className="ssc-label">Title Fee ($)</label>
            <input className="ssc-input" inputMode="numeric" value={titleFee} onChange={(e) => setTitleFee(e.target.value)} required data-testid="admin-fees-title" />
          </div>
          <div>
            <label className="ssc-label">Doc Fee Label</label>
            <input className="ssc-input" value={labelDoc} onChange={(e) => setLabelDoc(e.target.value)} />
          </div>
          <div>
            <label className="ssc-label">Title Fee Label</label>
            <input className="ssc-input" value={labelTitle} onChange={(e) => setLabelTitle(e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <label className="ssc-label">Effective Date (optional)</label>
            <input type="date" className="ssc-input max-w-xs" value={effDate || ""} onChange={(e) => setEffDate(e.target.value)} />
          </div>
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <button type="submit" disabled={busy} className="ssc-btn ssc-btn-primary" data-testid="admin-fees-save">
            {busy ? "Saving..." : "Save Fees"}
          </button>
          {savedMsg && (
            <span className="text-sm text-emerald-700 font-bold" data-testid="admin-fees-saved">{savedMsg}</span>
          )}
          {fees?.source && (
            <span className="text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
              source: {fees.source}
            </span>
          )}
        </div>
      </form>

      <section className="rounded-xl border border-[var(--ssc-line)] bg-white p-5 shadow-sm">
        <h2 className="font-extrabold text-[var(--ssc-navy)] text-lg">Audit History</h2>
        {audit.length === 0 ? (
          <p className="text-sm text-[var(--ssc-ink-2)] mt-2">No changes yet.</p>
        ) : (
          <table className="w-full text-sm mt-4 border-collapse" data-testid="admin-fees-audit">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wide text-[var(--ssc-ink-2)] border-b border-[var(--ssc-line)]">
                <th className="py-2 pr-3">When</th>
                <th className="py-2 pr-3">Who</th>
                <th className="py-2 pr-3">Doc Fee</th>
                <th className="py-2 pr-3">Title Fee</th>
              </tr>
            </thead>
            <tbody>
              {audit.map((a, i) => (
                <tr key={i} className="border-b border-[var(--ssc-line)]/50">
                  <td className="py-2 pr-3 text-[var(--ssc-ink-2)]">{a.changed_at?.slice(0, 19).replace("T", " ")}</td>
                  <td className="py-2 pr-3 font-bold">{a.changed_by}</td>
                  <td className="py-2 pr-3 tabular-nums">${a.before?.doc_fee ?? "—"} → <strong>${a.after?.doc_fee}</strong></td>
                  <td className="py-2 pr-3 tabular-nums">${a.before?.title_fee ?? "—"} → <strong>${a.after?.title_fee}</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
